import { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Button,
  Card,
  Container,
  Drawer,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircleFilled,
  IconCloudNetwork,
  IconScan,
} from '@tabler/icons-react';

import { useAuthContext, useSubnets, useNetworkScans } from '../../hooks';
import networkImage from './network.jpg';

export const Subnets = () => {
  const { token, isAuthenticated, logout } = useAuthContext();
  const { subnets, loading, error, fetchSubnets, startScan } = useSubnets(
    {
      token: token || undefined,
      onAuthError: logout,
    },
    {
      autoFetch: false,
    },
  );
  const { scans, fetchScans } = useNetworkScans({
    token: token || undefined,
    onAuthError: logout,
  });

  const hasFetchedRef = useRef(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selectedSubnetId, setSelectedSubnetId] = useState<number | null>(null);

  useEffect(() => {
    if (isAuthenticated && token && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchSubnets();
    }
  }, [isAuthenticated, token, fetchSubnets]);

  const handleStartScan = async (subnetId: number) => {
    try {
      await startScan(subnetId);
      // Could add a notification here about scan started
    } catch (error) {
      console.error('Failed to start scan:', error);
      // Could add error notification here
    }
  };

  const handleScansClick = async (subnetId: number) => {
    try {
      setSelectedSubnetId(subnetId);
      await fetchScans(subnetId);
      setDrawerOpened(true);
    } catch (error) {
      console.error('Failed to fetch scans:', error);
      // Could add error notification here
    }
  };

  if (loading) {
    return <div>Loading subnets...</div>;
  }

  if (error) {
    return <div>Error loading subnets: {error.message}</div>;
  }

  return (
    <Container>
      {subnets.length === 0 ? (
        <Alert
          variant="light"
          color="yellow"
          title="No subnets found"
          icon={<IconAlertCircleFilled />}
        >
          There aren't any subnets defined.
        </Alert>
      ) : (
        <SimpleGrid>
          {subnets.map((subnet) => (
            <Card key={subnet.id}>
              <Card.Section>
                <Image
                  src={networkImage}
                  radius="md"
                  fit="contain"
                  w="100%"
                  alt="Network"
                />
              </Card.Section>
              <Card.Section>
                <Stack gap="md" align="center" pt="md">
                  <>
                    <Title order={2}>
                      {subnet.address}/{subnet.mask_bits}
                    </Title>
                    <Text>Subnet Mask: {subnet.subnet_mask}</Text>
                    <Text>
                      Total Available Addresses:{' '}
                      {subnet.total_available_addresses}
                    </Text>
                  </>
                </Stack>
              </Card.Section>
              <Card.Section>
                <Group justify="space-between" pt="md">
                  <Button
                    onClick={() => handleScansClick(subnet.id)}
                    variant="filled"
                    size="md"
                    radius="md"
                    leftSection={<IconCloudNetwork />}
                  >
                    Scans
                  </Button>
                  <Button
                    onClick={() => handleStartScan(subnet.id)}
                    variant="outline"
                    size="md"
                    radius="md"
                    leftSection={<IconScan />}
                  >
                    Scan Now
                  </Button>
                </Group>
              </Card.Section>
            </Card>
          ))}
        </SimpleGrid>
      )}
      
      <Drawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        title="Network Scans"
        position="right"
        size="md"
      >
        <Stack>
          {scans.length === 0 ? (
            <Text>No scans found for this subnet.</Text>
          ) : (
            scans.map((scan) => (
              <Card key={scan.id} shadow="sm" padding="lg" radius="md" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text fw={500}>Scan #{scan.id}</Text>
                  <Text size="sm" c="dimmed">
                    {scan.scan_status}
                  </Text>
                </Group>
                <Text size="sm" mb="xs">
                  Started: {new Date(scan.started_at).toLocaleString()}
                </Text>
                {scan.ended_at && (
                  <Text size="sm" mb="xs">
                    Ended: {new Date(scan.ended_at).toLocaleString()}
                  </Text>
                )}
                <Button
                  variant="light"
                  size="xs"
                  onClick={() => window.open(`/scans/${scan.id}`, '_blank')}
                >
                  View Details
                </Button>
              </Card>
            ))
          )}
        </Stack>
      </Drawer>
    </Container>
  );
};
